import { useAuth, useUser } from '@clerk/clerk-react';
import { useState } from 'react';
import { Input } from '@/components/Input';
import { apiFetch } from '@/lib/api';
import { normalizeUsPhoneNumber } from '@/lib/phone';
import { track } from '@/lib/analytics';

export function DivorcePhoneActivation() {
  const { getToken } = useAuth();
  const { user, isLoaded } = useUser();
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [phoneId, setPhoneId] = useState<string | null>(null);
  const [phase, setPhase] = useState<'phone' | 'code' | 'done'>('phone');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!isLoaded || !user || user.primaryPhoneNumber || phase === 'done') return null;

  const sendCode = async () => {
    const normalized = normalizeUsPhoneNumber(phone);
    if (!normalized) {
      setError('Enter a valid U.S. phone number.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const existing = user.phoneNumbers.find(
        (item) => item.phoneNumber === normalized,
      );
      const number = existing ?? (await user.createPhoneNumber({ phoneNumber: normalized }));
      await number.prepareVerification();
      setPhoneId(number.id);
      setPhase('code');
      track('Divorce Harbor App Phone Code Sent');
    } catch (err) {
      const value = err as { errors?: { longMessage?: string; message?: string }[] };
      setError(
        value.errors?.[0]?.longMessage ??
          value.errors?.[0]?.message ??
          'Could not send a phone code. If this number already has a Harbor account, sign in with that account.',
      );
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    const number = user.phoneNumbers.find((item) => item.id === phoneId);
    if (!number) {
      setError('Phone verification expired. Start again.');
      setPhase('phone');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const result = await number.attemptVerification({ code: code.trim() });
      if (result.verification.status !== 'verified') {
        throw new Error('That code could not be verified.');
      }
      await user.update({ primaryPhoneNumberId: number.id });
      await user.reload();
      const response = await apiFetch(getToken, '/v1/user/sync-clerk', {
        method: 'POST',
      });
      if (!response.ok) throw new Error('Could not finish activating the mobile app.');
      setPhase('done');
      track('Divorce Harbor App Phone Verified');
    } catch (err) {
      setError(err instanceof Error ? err.message : "That code didn't work.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className='mt-6 rounded-xl border border-gray-200 bg-gray-50 p-4'>
      <h4 className='font-bold text-gray-900'>Activate your included year of Harbor</h4>
      <p className='mt-1 text-sm text-gray-600'>
        Harbor’s mobile app uses a verified phone number for co-parent invitations.
        Add yours here, then sign in to the app with this number.
      </p>
      {phase === 'phone' ? (
        <div className='mt-4 space-y-3'>
          <Input
            type='tel'
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            autoComplete='tel'
            placeholder='(555) 555-0100'
          />
          <button
            type='button'
            onClick={() => void sendCode()}
            disabled={busy}
            className='w-full rounded-xl border border-primary px-4 py-3 font-semibold text-primary disabled:opacity-50'
          >
            {busy ? 'Sending…' : 'Text me a code'}
          </button>
        </div>
      ) : (
        <div className='mt-4 space-y-3'>
          <Input
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode='numeric'
            autoComplete='one-time-code'
            placeholder='6-digit code'
          />
          <button
            type='button'
            onClick={() => void verify()}
            disabled={busy}
            className='w-full rounded-xl border border-primary px-4 py-3 font-semibold text-primary disabled:opacity-50'
          >
            {busy ? 'Verifying…' : 'Verify phone'}
          </button>
        </div>
      )}
      {error ? <p className='mt-3 text-sm font-medium text-red-600'>{error}</p> : null}
    </div>
  );
}
